import React, { useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";

/**
 * WS-11 Final Launch Reconciliation — language-aware redirect for the
 * four legacy `/legal/*` URLs.
 *
 * Behavior:
 *   * Resolves the user's active language from (in order):
 *       1. `?lang=` query parameter (`de` or `en`) if present and valid
 *       2. `localStorage["stockaily_locale"]` if set to `de` or `en`
 *       3. `document.documentElement.lang` if set to `de` or `en`
 *       4. `navigator.language` prefix (`de` → German)
 *       5. Default: German (per Owner directive — DE is the fallback)
 *   * Emits `<meta name="robots" content="noindex">` on the legacy
 *     path before navigating so search engines do not treat legacy
 *     URLs as separate canonical pages.
 *   * Uses `<Navigate replace>` so the legacy URL does not remain in
 *     browser history (SPA-equivalent of a permanent redirect).
 *   * The `fallback` prop is retained ONLY as a defensive render if a
 *     future edit removes `Navigate` from react-router; in normal
 *     operation the component never returns the fallback.
 */
function _resolveLanguage() {
  try {
    // 1. Query parameter
    const qs = new URLSearchParams(window.location.search);
    const q = (qs.get("lang") || "").toLowerCase();
    if (q === "de" || q === "en") return q;
    // 2. Persisted user preference
    const stored = (localStorage.getItem("stockaily_locale") || "").toLowerCase();
    if (stored === "de" || stored === "en") return stored;
    // 3. Document lang attribute
    const html = (document.documentElement.lang || "").toLowerCase();
    if (html.startsWith("de")) return "de";
    if (html.startsWith("en")) return "en";
    // 4. Browser language
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("de")) return "de";
    if (nav.startsWith("en")) return "en";
  } catch (_e) {
    /* localStorage / navigator may throw in constrained contexts */
  }
  // 5. Default (Owner directive)
  return "de";
}

export default function LegalRedirect({
  canonicalDe,
  canonicalEn,
  legacy,
  fallback = null,
}) {
  const target = useMemo(() => {
    const lang = _resolveLanguage();
    return lang === "en" ? canonicalEn : canonicalDe;
  }, [canonicalDe, canonicalEn]);

  useEffect(() => {
    // Emit noindex on the legacy path so it is not indexed as a
    // duplicate canonical. React-Helmet is not a dependency; use a
    // direct DOM insertion scoped to this mount lifecycle.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    meta.setAttribute("data-legal-redirect", legacy || "");
    document.head.appendChild(meta);
    return () => {
      try {
        document.head.removeChild(meta);
      } catch (_e) {
        /* already removed */
      }
    };
  }, [legacy]);

  if (!target) {
    return fallback;
  }
  return (
    <Navigate
      to={target}
      replace
      data-testid={`legal-redirect-${(legacy || "").replace(/\W+/g, "-")}`}
    />
  );
}
